/**
 * Cleared Title Statuses filter panel for the Wholesale Workspace.
 *
 * Shows the hierarchy of Title Statuses present in the current Tekion
 * file, grouped by family (first whitespace-delimited token):
 *   YES (456)
 *     YES VTD (456)
 *   PROBLEM (38)
 *     PROBLEM VTD (38)
 *
 * Whitespace variants of the same Title Status collapse into one row
 * via the canonical key (strip outer + collapse internal whitespace +
 * uppercase) — matches the backend's canonicalize_for_match. The
 * collapsed row's count is the sum across all variants.
 *
 * Toggling a family row writes to selectedFamilies; toggling a child
 * row writes to perValue. Per-value entries override the family rule
 * server-side (services/wholesale/title_status.py is_cleared).
 *
 * The row primitive (label + checkbox + label, padding xs/md, fontSize
 * 13, gap sm) mirrors components/audit/MultiSelectDropdown.tsx ~line
 * 108-117 (itemStyle).
 */
import React from "react"
import { ChevronDown, ChevronRight } from "lucide-react"

import { THEME } from "../../config/theme"

export interface TitleStatusValue {
  canonical: string
  count: number
}

export interface TitleStatusGroup {
  family: string
  totalCount: number
  values: TitleStatusValue[]
}

interface Props {
  groups: TitleStatusGroup[]
  selectedFamilies: string[]
  perValue: Record<string, boolean>
  expanded: boolean
  onToggleExpanded: () => void
  onToggleFamily: (family: string) => void
  onTogglePerValue: (canonical: string, nextChecked: boolean) => void
  loading: boolean
  error: string | null
}

export default function FilterPanel({
  groups,
  selectedFamilies,
  perValue,
  expanded,
  onToggleExpanded,
  onToggleFamily,
  onTogglePerValue,
  loading,
  error,
}: Props) {
  return (
    <div
      style={{
        border: `1px solid ${THEME.colors.silver}`,
        borderRadius: THEME.borderRadius.md,
        background: THEME.colors.neutral.light.surface,
        fontFamily: THEME.fonts.primary,
        overflow: "hidden",
      }}
    >
      <div
        onClick={onToggleExpanded}
        style={{
          display: "flex",
          alignItems: "center",
          gap: THEME.spacing.sm,
          padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
          cursor: "pointer",
          userSelect: "none",
          fontSize: 13,
          fontWeight: 600,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        {expanded ? (
          <ChevronDown size={14} strokeWidth={2} style={{ color: THEME.colors.neutral.light.textSecondary }} />
        ) : (
          <ChevronRight size={14} strokeWidth={2} style={{ color: THEME.colors.neutral.light.textSecondary }} />
        )}
        Cleared Title Statuses
      </div>

      {expanded && (
        <div style={{ padding: `0 0 ${THEME.spacing.md}` }}>
          {loading && (
            <div style={{ ...messageStyle, color: THEME.colors.neutral.light.textSecondary }}>
              Loading…
            </div>
          )}
          {error && !loading && (
            <div style={{ ...messageStyle, color: THEME.colors.semantic.danger }}>
              {error}
            </div>
          )}

          {!loading && !error && groups.length === 0 && (
            <div style={{ ...messageStyle, color: THEME.colors.neutral.light.textSecondary }}>
              No Title Statuses to filter — upload a Tekion file to begin.
            </div>
          )}

          {!loading && !error && groups.length > 0 && (
            <div>
              {groups.map((group) => {
                const familyChecked = selectedFamilies.includes(group.family)
                return (
                  <div key={group.family}>
                    <Row
                      indent={0}
                      weight={500}
                      label={group.family}
                      count={group.totalCount}
                      checked={familyChecked}
                      onToggle={() => onToggleFamily(group.family)}
                    />
                    {group.values.map((value) => {
                      const explicit = perValue[value.canonical]
                      const checked = explicit !== undefined ? explicit : familyChecked
                      return (
                        <Row
                          key={value.canonical}
                          indent={1}
                          weight={400}
                          label={value.canonical}
                          count={value.count}
                          checked={checked}
                          onToggle={() => onTogglePerValue(value.canonical, !checked)}
                        />
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface RowProps {
  indent: 0 | 1
  weight: 400 | 500
  label: string
  count: number
  checked: boolean
  onToggle: () => void
}

function Row({ indent, weight, label, count, checked, onToggle }: RowProps) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
        paddingLeft:
          indent === 0
            ? THEME.spacing.md
            : `calc(${THEME.spacing.md} + ${THEME.spacing.lg})`,
        fontSize: 13,
        color: THEME.colors.neutral.light.textPrimary,
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        style={{ cursor: "pointer" }}
      />
      <span style={{ fontWeight: weight }}>{label}</span>
      <span style={{ color: THEME.colors.neutral.light.textSecondary, fontWeight: 400 }}>
        ({count.toLocaleString()})
      </span>
    </label>
  )
}

const messageStyle: React.CSSProperties = {
  padding: `0 ${THEME.spacing.md}`,
  fontSize: 13,
}
